// @ts-strict-ignore
/**
 * Multi metrics menu: clickable list of metrics(events, profile, funnels, retention, formula, cohorts) to go inside mp-drop-menu
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';

import {useDomEvent} from 'common/hooks/useDomEvent';
import {useCohorts} from 'common/contexts/react/cohorts-context';
import {ButtonTheme} from 'common/components/button/constants';
import {useDataGroups} from 'common/contexts/react/data-group-context';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {PositioningStrategy} from 'common/components/tooltip/types';
import {Directions} from 'common/components/drop-menu/types';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {ContextPanelReact} from 'common/widgets/context-panel';
import {emitTrackEvent} from 'common/util/dom';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {promptCustomEvents} from 'common/widgets/custom-event-modal/utils';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {userDetailsStore} from 'common/report/stores/user-details';
import {Cohort as CohortModel} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {promptInlineCohorts} from 'common/widgets/inline-cohorts-modal/util';
import {ResourceType} from 'common/report/constants';
import {useTranslation} from 'common/i18n/hooks';

import {NewQueryBlock} from '../new-query-block';
import {SelectorType} from '../constants';
import {getCustomEventMenuItem} from '../contexts/events-controller';
import {commitCohort} from '../properties-menu/util';
import {EventItem} from '../events-menu/types';
import {InlineCohortSegment} from '../segment-menu/types';
import {MultiMetricsMenu} from './index';
import {CohortItemValue, CohortSectionItem, EventSectionItem, isCohortMenuSectionItem, MenuType} from './types';
import {useSelectedLabel} from './hooks';
import {createContextPanelEventHandlers, createInlineCohortModalAttrs, getTooltipData} from './utils';
import {TrackingEvent} from './tracking';

type Props = Pick<
  React.ComponentProps<typeof MultiMetricsMenu>,
  | 'selected'
  | 'allDataGroups'
  | 'dataGroupId'
  | 'descriptionEditDisabled'
  | 'excludeAutotrackEvents'
  | 'excludeCustomEvents'
  | 'excludedMetricsRowTypes'
  | 'hideEditControls'
  | 'projectHasPeople'
  | 'projectHasCohorts'
  | 'showAllEvents'
  | 'showAnyEvent'
  | 'showSessionEvent'
  | 'showSpecialDatasetProperty'
  | 'showTopEvents'
  | 'supportB2bCohorts'
  | 'trackName'
  | 'projectId'
  | 'workspaceId'
  | 'userId'
  | 'onSelect'
  | 'onCreate'
  | 'onTrack'
> &
  Pick<
    React.ComponentProps<typeof NewQueryBlock>,
    'className' | 'elref' | 'open' | 'leftIcon' | 'buttonSize' | 'theme' | 'readonly'
  > & {
    contextPanelParentElement: HTMLElement;
    onOpen?: () => void;
    onClose?: () => void;
    onRemove?: () => void;
  };

export function MultiMetricsQueryBlock(props: Props) {
  const ref = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(props.open);
  const [selectedItemTooltipData, setSelectedItemTooltipData] = useState<ContextPanelData>(null);

  const eventsController = useEventsController();
  const cohortsStore = useCohorts();
  const dataGroupStore = useDataGroups();
  const currentProject = useCurrentProject();
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const i18n = useTranslation();

  const {t, print} = i18n;
  const userId = props.userId || userCookieUtils.getUserIdentityFromCookie()?.id;

  useDomEvent(ref.current, `track`, props.onTrack);

  useEffect(() => {
    const fetchCohorts = async () => {
      await cohortsStore.fetch({projectId: props.projectId, workspaceId: props.workspaceId});
    };
    fetchCohorts();
  }, [props.projectId, props.workspaceId, props.projectHasCohorts]);
  useEffect(() => {
    if (typeof eventsController.loading === `undefined`) {
      eventsController.fetchData();
    }
  }, [eventsController]);
  useEffect(() => {
    dataGroupStore.fetch({
      projectId: props.projectId,
      workspaceId: props.workspaceId,
    });
  }, [props.projectId, props.workspaceId]);
  useEffect(() => {
    userDetailsStore.fetch();
  }, []);

  const selectedLabel = useSelectedLabel({selected: props.selected, dataGroupId: props.dataGroupId});
  const label = useMemo(
    () => selectedLabel,
    [props.selected, props.dataGroupId, Object.keys(cohortsStore.value).length],
  );
  const isUnsaved = useMemo(() => {
    if (props.selected && isCohortMenuSectionItem(props.selected)) {
      const cohortItem = props.selected.value;
      return Boolean(typeof cohortItem.value !== `number` && !!cohortItem.value.cohort);
    }
    return false;
  }, [props.selected]);
  const dataGroupName = useMemo(
    () => dataGroupStore?.value?.[props.dataGroupId]?.displayName || t(`query-builder.dg-user`),
    [props.dataGroupId],
  );

  // TODO - mixpanel.track
  const track = (eventName, extraProps = {}, {cancelable = false} = {}) => {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
    };
    if (props.trackName) {
      additionalProps[`tab`] = props.trackName;
    }
    emitTrackEvent(ref.current, {
      props: {
        ...extraProps,
        ...additionalProps,
      },
      eventName,
      cancelable,
    });
  };

  const handleMenuChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      props.onOpen();
      track(TrackingEvent.OpenMetricsDropDown);
    } else {
      props.onClose();
    }
  };

  const handleShowContextPanel = () => {
    const tooltipData = getTooltipData(props.selected, {
      readonly: props.readonly,
      dataGroupName,
      descriptionEditDisabled: props.descriptionEditDisabled,
      hideEditControls: props.hideEditControls,
      eventsController,
      cohortsStore,
      currentProject: currentProject.get()?.value,
      behaviors,
      metrics,
    });
    setSelectedItemTooltipData(tooltipData);
  };

  const handleInlineEvent = async (customEventId: null | number): Promise<void> => {
    const {appliedValue, deletedId} = await promptCustomEvents({
      open: true,
      'all-data-groups': props.allDataGroups,
      'data-group-id': props.dataGroupId ?? ``,
      'project-has-people': props.projectHasPeople,
      'project-id': props.projectId,
      'workspace-id': props.workspaceId,
      'track-name': props.trackName,
      ...(customEventId ? {'selected-custom-event-id': customEventId} : {}),
    });
    track(TrackingEvent.OpenCustomEventsModal);

    if (appliedValue) {
      const item = getCustomEventMenuItem(appliedValue);
      const sectionItem: EventSectionItem = {
        icon: item.icon,
        label: item.label,
        menuType: MenuType.Event,
        value: item.value,
      };
      // TODOCC add to cache...
      setOpen(false);
      props.onSelect(sectionItem);
    } else if (deletedId && (props.selected?.value as EventItem)?.id === deletedId) {
      props.onRemove();
    }
  };

  const handleInlineCohort = async (cohortItem): Promise<void> => {
    let defaultCohort: CohortModel;
    if (cohortItem) {
      const cohortParsed = cohortItem.id
        ? cohortsStore.value[cohortItem.id]
        : cohortItem.value?.cohort?.raw_cohort || cohortItem.value?.cohort;
      defaultCohort = CohortModel.parseCohort(cohortParsed);
    } else {
      defaultCohort = new CohortModel({...new CohortModel(), dataGroupId: props.dataGroupId});
    }
    const attrs = createInlineCohortModalAttrs({
      defaultCohort,
      dataGroupId: props.dataGroupId,
      userId,
      projectId: Number(props.projectId),
      workspaceId: Number(props.workspaceId),
      projectHasCohorts: props.projectHasCohorts,
      supportB2bCohorts: props.supportB2bCohorts,
      trackName: props.trackName,
    });
    const {cohortStagedValue, cohortSavedValue} = await promptInlineCohorts(attrs);
    const cohortValue = cohortStagedValue || cohortSavedValue;
    track(TrackingEvent.OpenInlineCohortsModal);

    if (cohortValue) {
      const createdBy = {
        id: userId,
        name: userDetailsStore?.value.name,
        email: userDetailsStore?.value.email,
      };
      const {commitedItem, isSaved, isEditingTemporaryCohort} = commitCohort(cohortsStore, createdBy, cohortValue);
      let itemValue: CohortItemValue;
      if (typeof commitedItem.value === `number`) {
        itemValue = {
          ...(props.dataGroupId ? {dataGroupId: props.dataGroupId} : {}),
          resourceType: ResourceType.Cohort,
          label: print(commitedItem.label),
          value: commitedItem.value,
        };
      } else {
        itemValue = {
          resourceType: ResourceType.Cohort,
          label: print(commitedItem.label),
          value: commitedItem.value as InlineCohortSegment,
        };
      }
      const sectionItem: CohortSectionItem = {
        icon: `community`,
        label: commitedItem.label,
        menuType: MenuType.Cohort,
        value: itemValue,
      };

      if (isSaved) {
        track(TrackingEvent.SaveInlineCohorts);
      } else if (!isEditingTemporaryCohort) {
        track(TrackingEvent.CreateInlineCohort);
      } else {
        track(TrackingEvent.EditInlineCohorts);
      }

      // TODOCC add to cache...
      setOpen(false);
      props.onSelect(sectionItem);
    }
  };

  const contextPanelEventHandlers = createContextPanelEventHandlers({
    track,
    handleInlineEvent: (customEventId) => handleInlineEvent(customEventId),
    handleInlineCohort: (cohort) => handleInlineCohort(cohort),
    additionalArgs: {projectId: props.projectId, workspaceId: props.workspaceId},
    availableContexts: {behaviors, metrics},
  });

  return (
    <NewQueryBlock
      ref={ref}
      label={label}
      buttonSize={props.buttonSize}
      className={clsx(`type-multi-metrics`, props.className)}
      dropMenuContainerClass='for-multi-metrics-menu'
      elref={props.elref}
      open={open}
      onChangeOpen={handleMenuChange}
      readonly={props.readonly}
      theme={ButtonTheme.QueryBuilder}
      selectorType={SelectorType.Button}
      leftIcon={props.leftIcon}
      isUnsaved={isUnsaved}
      onShowContextPanel={handleShowContextPanel}
      contextPanelElement={
        <ContextPanelReact
          positionStrategy={PositioningStrategy.Fixed}
          placement={Directions.Right}
          offset={8}
          showArrow
          elref={`contextPanel`}
          data={selectedItemTooltipData}
          positionParent={props.contextPanelParentElement}
          onTooltipContextAction={contextPanelEventHandlers.onTooltipContextAction}
          onTooltipContextEditDescription={contextPanelEventHandlers.onTooltipContextEditDescription}
        />
      }
      menuElement={
        open && (
          <MultiMetricsMenu
            selected={props.selected}
            dataGroupName={dataGroupName}
            allDataGroups={props.allDataGroups}
            dataGroupId={props.dataGroupId}
            excludeAutotrackEvents={props.excludeAutotrackEvents}
            excludeCustomEvents={props.excludeCustomEvents}
            excludedMetricsRowTypes={props.excludedMetricsRowTypes}
            descriptionEditDisabled={props.descriptionEditDisabled}
            hideEditControls={props.hideEditControls}
            projectHasCohorts={props.projectHasCohorts}
            projectHasPeople={props.projectHasPeople}
            readonly={props.readonly}
            showAllEvents={props.showAllEvents}
            showAnyEvent={props.showAnyEvent}
            showSessionEvent={props.showSessionEvent}
            showSpecialDatasetProperty={props.showSpecialDatasetProperty}
            showTopEvents={props.showTopEvents}
            supportB2bCohorts={props.supportB2bCohorts}
            trackName={props.trackName}
            userId={userId}
            projectId={props.projectId}
            workspaceId={props.workspaceId}
            onUpdateInlineEvent={handleInlineEvent}
            onUpdateInlineCohort={handleInlineCohort}
            onTrack={props.onTrack}
            onSelect={(selection) => {
              setOpen(false);
              props.onSelect(selection);
            }}
            onCreate={props.onCreate}
            onDelete={props.onRemove}
          />
        )
      }
    />
  );
}
